/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { getAppearanceForAppType } from '@atlaskit/avatar';
import { B400, N800, N200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import React from 'react';
import { type User } from '../types';
import { AvatarItemOption, textWrapper } from './AvatarItemOption';
import { HighlightText } from './HighlightText';
import { AvatarOrIcon } from './AvatarOrIcon';
import { SizeableAvatar } from './SizeableAvatar';
import { hasValue } from './utils';
import { fg } from '@atlaskit/platform-feature-flags';

export type UserOptionProps = {
	isSelected: boolean;
	status?: string;
	user: User;
};

const styles = (color: string) =>
	css({
		display: 'inline',
		verticalAlign: 'top',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		color,
	});

export class UserOption extends React.PureComponent<UserOptionProps> {
	getPrimaryText = () => {
		const {
			user: { name, publicName, highlight },
		} = this.props;

		const result = [
			<span
				key="name"
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css={textWrapper(
					this.props.isSelected ? token('color.text.selected', B400) : token('color.text', N800),
				)}
			>
				<HighlightText highlights={highlight && highlight.name}>{name}</HighlightText>
			</span>,
		];
		if (hasValue(publicName) && name.trim() !== publicName.trim()) {
			const color = this.props.isSelected
				? token('color.text.selected', B400)
				: token('color.text.subtlest', N200);

			result.push(
				<React.Fragment key="publicName">
					{' '}
					<span
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						css={fg('platform_user_picker_firefox_tab_fix') ? styles(color) : textWrapper(color)}
						// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
					>
						(
						<HighlightText highlights={highlight && highlight.publicName}>
							{publicName}
						</HighlightText>
						)
					</span>
				</React.Fragment>,
			);
		}
		return result;
	};

	renderSecondaryText = () =>
		this.props.user.byline ? (
			<span
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css={textWrapper(
					this.props.isSelected
						? token('color.text.selected', B400)
						: token('color.text.subtlest', N200),
				)}
			>
				{this.props.user.byline}
			</span>
		) : undefined;

	private renderAvatar = () => {
		const {
			user: { avatarUrl, appType, icon, iconColor },
			status,
		} = this.props;

		const appearance = fg('platform-component-visual-refresh') ? 'medium' : 'big';
		// Only use icon if feature gate is enabled
		if (icon && fg('atlaskit_user_picker_support_icon')) {
			return (
				<AvatarOrIcon
					appearance={appearance}
					icon={icon}
					iconColor={iconColor}
					src={avatarUrl}
					presence={status}
					avatarAppearanceShape={
						fg('jira_ai_agent_avatar_user_picker_user_option')
							? getAppearanceForAppType(appType)
							: undefined
					}
				/>
			);
		}
		// Fallback to original behavior
		return (
			<SizeableAvatar
				appearance={appearance}
				src={avatarUrl}
				presence={status}
				avatarAppearanceShape={
					fg('jira_ai_agent_avatar_user_picker_user_option')
						? getAppearanceForAppType(appType)
						: undefined
				}
			/>
		);
	};

	private getLozengeProps = () =>
		typeof this.props.user.lozenge === 'string'
			? {
					text: this.props.user.lozenge,
				}
			: this.props.user.lozenge;

	render(): React.JSX.Element {
		return (
			<AvatarItemOption
				avatar={this.renderAvatar()}
				lozenge={this.getLozengeProps()}
				isDisabled={this.props.user.isDisabled}
				primaryText={this.getPrimaryText()}
				secondaryText={this.renderSecondaryText()}
			/>
		);
	}
}
